import React from 'react';

import useZenMode from '../../../hooks/useZenMode';
import { cardShows, dropStep } from './density';

// A card rendered outside a provider — a preview, a hover card — shows
// everything rather than guessing at a size it was never measured for.
const CardDensityContext = React.createContext(() => true);

/**
 * Measures the card and decides which of its parts still fit. Attach
 * `cardRef` to the card root and wrap its children in `CardDensityProvider`,
 * or the parts below it never hear the answer.
 */
export default function useCardDensity() {
  const cardRef = React.useRef(null);
  const zenMode = useZenMode();
  const [step, setStep] = React.useState(0);

  React.useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;
    const measure = () => setStep(dropStep(card.clientWidth, card.clientHeight));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(card);
    return () => observer.disconnect();
  }, []);

  const shows = React.useCallback(
    (part) => cardShows(part, step, zenMode),
    [step, zenMode],
  );

  return { cardRef, shows };
}

export function CardDensityProvider({ shows, children }) {
  return (
    <CardDensityContext.Provider value={shows}>
      {children}
    </CardDensityContext.Provider>
  );
}

/**
 * The predicate, for the component that renders the part. A part asks for
 * itself, the way each piece of chrome reads zen mode for itself.
 */
export function useCardShows() {
  return React.useContext(CardDensityContext);
}
